import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { field, fieldTypes, keys } from '@enums';
import ZipCode from './ZipCode';
import HomeValue from './HomeValue';
import LoanAmount from './LoanAmount';
import CreditScore from './CreditScore';
import PropertyType from './PropertyType';
import OccupancyType from './OccupancyType';
import CashOutAmount from './CashOutAmount';
import useConfig from '@hooks/useConfig';
import usePrevious from '@hooks/usePrevious';
import useIsMobile from '@hooks/useIsMobile';
import { isFunction, debounce } from 'lodash';
import {
  FORM_FIELDS,
  VALIDATE_ZIP_CODE_ENDPOINT,
  DEFAULT_ZIP_CODE_ERROR,
  ZIP_CODE_VALIDATION_DEBOUNCE_TIME,
  MAX_ZIP_CODE_VALIDATIONS_BEFORE_DEBOUNCE
} from '@constants';

export default React.memo(function FieldComponents({
  state,
  errors,
  showInlineErrors,
  cachedZipCodes,
  addCachedZipCode,
  canRefresh,
  validations = {},
  canValidate,
  inputsAreOpen,
  onChange,
  updateErrors,
  setCanValidate,
  setCurrentInput,
  refresh,
  prefetchScenario
}) {
  //////////////////////////
  // Prefetch
  //////////////////////////
  const { scenarioPrefetchDelay } = useConfig();
  const prefetchTimer = useRef();

  useEffect(() => {
    clearTimeout(prefetchTimer.current);

    if (scenarioPrefetchDelay && scenarioPrefetchDelay > 0) {
      prefetchTimer.current = setTimeout(() => {
        if (canRefresh) {
          prefetchScenario(state);
        }
      }, scenarioPrefetchDelay);
    }

    let timer = prefetchTimer.current;
    return () => clearTimeout(timer);
  }, [canRefresh, state, prefetchScenario, scenarioPrefetchDelay]);

  //////////////////////////
  // Field State
  //////////////////////////

  const stateRef = useRef(state);
  const cachedZipCodesRef = useRef({});

  useEffect(() => (stateRef.current = state), [state]);
  useEffect(
    () => (cachedZipCodesRef.current = cachedZipCodes),
    [cachedZipCodes]
  );

  const zipCodeCount = useRef(0);
  const checkZipCode = useCallback(
    async zipCode => {
      if (stateRef.current.zipCode !== zipCode) {
        return;
      }

      const response = await fetch(VALIDATE_ZIP_CODE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Encoding': 'gzip, deflate',
          Accept: '*/*'
        },
        body: JSON.stringify({ zipCode })
      });

      const data = await response.json();

      if (stateRef.current.zipCode === zipCode) {
        updateErrors(
          data.isValid ? null : data.message ?? DEFAULT_ZIP_CODE_ERROR,
          keys.FOOTPRINT
        );
      }

      addCachedZipCode(zipCode, data);
    },
    [addCachedZipCode, updateErrors]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCheckZipCode = useCallback(
    debounce(checkZipCode, ZIP_CODE_VALIDATION_DEBOUNCE_TIME, {
      leading: true,
      trailing: true
    }),
    [checkZipCode]
  );

  const validateZipCodeInFootprint = useCallback(
    async zipCode => {
      zipCode = String(zipCode);

      const cachedData = cachedZipCodesRef.current?.[zipCode];

      if (cachedData) {
        updateErrors(
          cachedData.isValid
            ? null
            : cachedData.message ?? DEFAULT_ZIP_CODE_ERROR,
          keys.ZIP_CODE
        );

        return;
      }

      try {
        const fn =
          zipCodeCount.current > MAX_ZIP_CODE_VALIDATIONS_BEFORE_DEBOUNCE
            ? debouncedCheckZipCode
            : checkZipCode;

        fn(zipCode);

        zipCodeCount.current++;
      } catch (error) {
        console.error(error);
      }
    },
    [updateErrors, checkZipCode, debouncedCheckZipCode]
  );

  const validate = useCallback(
    (value, name, currentState = state) => {
      const { validate } = controls[name];
      const fn = isFunction(validate) ? validate : validate.fn;
      const dependencies = validate.dependencies;
      const _state = { ...currentState };
      const errorMessage = fn?.(value, _state, validations) ?? null;

      if (fn) {
        updateErrors(errorMessage, name);
      }

      if (dependencies) {
        [dependencies].flat().forEach(key => {
          const { validate } = controls[key];
          const fn = isFunction(validate) ? validate : validate.fn;

          if (fn) {
            const errorMessage = fn(_state[key], _state, validations) ?? null;
            updateErrors(errorMessage, key);
          }
        });
      }

      if (name === keys.ZIP_CODE && !errorMessage) {
        validateZipCodeInFootprint(value);
      }
    },
    [updateErrors, state, validations, validateZipCodeInFootprint]
  );

  const prevState = usePrevious(state);

  useEffect(() => {
    if (state && prevState) {
      Object.keys(controls).forEach(key => {
        if (
          state[key] !== prevState[key] ||
          state.loanPurpose !== prevState.loanPurpose
          // (state.loanPurpose !== prevState.loanPurpose &&
          //   key === keys.CASH_OUT_AMOUNT)
        ) {
          validate(state[key], key, state);
        }
      });
    }
  }, [state, prevState, validate]);

  const handleFocus = useCallback(
    (_value, name) => {
      clearTimeout(prefetchTimer.current);
      setCurrentInput(name);
    },
    [setCurrentInput]
  );

  const handleBlur = useCallback(
    (_value, name) => {
      if (!canValidate.includes(name)) {
        setCanValidate([...canValidate, name]);
      }
    },
    [canValidate, setCanValidate]
  );

  const handleChange = useCallback(
    (value, name) => {
      onChange(value, name);

      if (name === keys.ZIP_CODE && String(value).length === 5) {
        setCanValidate([...canValidate, name]);
      }
    },
    [canValidate, onChange, setCanValidate]
  );

  const handleKeyDown = useCallback(
    (value, name, e) => {
      if (e.key === 'Enter') {
        refresh();
      }
    },
    [refresh]
  );

  const tabIndex = inputsAreOpen ? 0 : -1;
  const isMobile = useIsMobile();

  const fields = useMemo(() => {
    return FORM_FIELDS.map(name => {
      const { Component, dependencies, type } = controls[name];

      if (!Component) {
        return null;
      }

      const error = errors.find(e => {
        return (
          e.name === name ||
          (name === keys.ZIP_CODE && e.name === keys.FOOTPRINT)
        );
      });

      const props = {
        name,
        tabIndex,
        value: state[name],
        key: name,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onChange: handleChange,
        onKeyDown: handleKeyDown,
        hasError: !!error,
        errorMessage: error?.message ?? null,
        showErrorMessage: showInlineErrors
      };

      if (type === fieldTypes.SELECT && isMobile) {
        props.usePortal = false;
      }

      if (dependencies) {
        [dependencies].flat().forEach(key => {
          props[key] = state[key];
        });
      }

      return <Component {...props} />;
    });
  }, [
    state,
    tabIndex,
    isMobile,
    errors,
    showInlineErrors,
    handleBlur,
    handleChange,
    handleFocus,
    handleKeyDown
  ]);

  return fields;
});

// Object includes keys for each potential form field along
// with a validation function. When the field's value changes
// it will be passed to this function. It should return
// a string with an error message if it is invalid.
const controls = {
  [field.LOAN_AMOUNT]: LoanAmount,
  [field.ZIP_CODE]: ZipCode,
  [field.HOME_VALUE]: HomeValue,
  [field.CREDIT_SCORE]: CreditScore,
  [field.PROPERTY_TYPE]: PropertyType,
  [field.OCCUPANCY_TYPE]: OccupancyType,
  [field.CASH_OUT_AMOUNT]: CashOutAmount
};
